// src/components/Users.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Form, Nav, Badge } from 'react-bootstrap';
import { isAuthenticated, getUser } from '../utils/auth';
import PaginationControls from './PaginationControls';

const PAGE_SIZE = 20;

// users.status badges — distinct from StatusBadge (which covers
// registrations.status: pending/assigned/approved/rejected/deregistered).
// 'pending' keeps the "Requested" label users are already used to seeing
// for a not-yet-decided registration.
const USER_STATUS_MAP = {
  pending: { label: 'Requested', variant: 'warning' },
  active: { label: 'Active', variant: 'success' },
  inactive: { label: 'Inactive', variant: 'secondary' },
  rejected: { label: 'Rejected', variant: 'danger' },
};

const STATUS_TABS = [
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'pending', label: 'Pending' },
  { key: 'rejected', label: 'Rejected' },
];

// Same pattern as TrainingAttendeeList.js's sortIndicator — the other
// click-to-sort table in the app.
function sortIndicator(sortConfig, key) {
  if (sortConfig.key !== key) return '';
  return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
}

const Users = () => {

  const loggedInUser = getUser();
  console.log ("loggedinuser ", loggedInUser);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' }); // Sorting state
  const [filteredCompany, setFilteredCompany] = useState(''); // Filtering by company
  const [statusFilter, setStatusFilter] = useState('active'); // Filtering by status tab, default Active
  const [companies, setCompanies] = useState([]); // For the company filter dropdown, independent of the current page
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);

  // Base API URL from environment variable
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  console.log('API_BASE_URL:', API_BASE_URL);

  // Function to fetch users data (server-side paginated, sorted, filtered)
  const fetchUsers = () => {
    setLoading(true);
    const params = new URLSearchParams({
      page,
      sort: sortConfig.key,
      direction: sortConfig.direction,
    });
    if (filteredCompany) {
      params.set('company', filteredCompany);
    }
    if (statusFilter) {
      params.set('status', statusFilter);
    }
    fetch(`${API_BASE_URL}/users/get_users.php?${params.toString()}`,{credentials: 'include'})
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }
        // Handle empty or invalid JSON responses gracefully
        return response.text().then(text => {
          try {
            return JSON.parse(text);
          } catch (error) {
            console.log('text:', text);
            throw new Error('Invalid JSON response');
          }
        });
      })
      .then(data => {
        console.log('Fetched users:', data);
        if (Array.isArray(data.items)) {
          setUsers(data.items);
          setTotalPages(data.total_pages || 1);
          setTotalRows(data.total_rows || 0);
        } else {
          console.error('Expected a paginated envelope but got:', data);
          setUsers([]);
        }
        setLoading(false); // Set loading to false after fetching
      })
      .catch(err => {
        console.error('Error fetching users:', err);
        setError(err.message); // Set error message
        setLoading(false); // Set loading to false even on error
      });
  };

  useEffect(() => {
    fetchUsers(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sortConfig, filteredCompany, statusFilter]);

  // Company list for the filter dropdown — fetched independently of the
  // current page so it always shows every company, not just ones visible
  // on the current page of users. Skipped for trainingadmin, who never
  // sees the dropdown built from it (see render below).
  useEffect(() => {
    if (loggedInUser.role === 'trainingadmin') return;
    fetch(`${API_BASE_URL}/companies/get_companies.php`, { credentials: 'include' })
      .then(response => response.json())
      .then(data => {
        if (Array.isArray(data)) {
          setCompanies(data);
        }
      })
      .catch(error => console.error('Error fetching companies:', error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sorting function
  const sortUsers = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setPage(1);
  };

  // Filtering function
  const handleCompanyFilter = (e) => {
    setFilteredCompany(e.target.value);
    setPage(1);
  };

  const handleStatusTab = (key) => {
    setStatusFilter(key);
    setPage(1);
  };


  return (
    <div className='p-2'>
      <h2>User List</h2>

      {/* Company filter — meaningless for trainingadmin: the backend already
          scopes their list to their own vendor company only, so there's
          nothing else to filter by, and the dropdown otherwise leaks every
          other company's name to them for no reason. */}
      {loggedInUser.role !== 'trainingadmin' && (
        <Form.Group controlId="filterByCompany">
          <Form.Label>Filter by Company</Form.Label>
          <Form.Control as="select" value={filteredCompany} onChange={handleCompanyFilter}>
            <option value="">All Companies</option>
            {companies.map((company) => (
              <option key={company.id} value={company.name}>
                {company.name}
              </option>
            ))}
          </Form.Control>
        </Form.Group>
      )}

      <Nav variant="tabs" activeKey={statusFilter} onSelect={handleStatusTab} className="mb-2">
        {STATUS_TABS.map((tab) => (
          <Nav.Item key={tab.key}>
            <Nav.Link eventKey={tab.key}>{tab.label}</Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      {loading ? (
            <p>Loading...</p> // Display loading message while fetching
          ) : error ? (
            <p>Error: {error}</p> // Display error message if there is an error
          ) : (
      <>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>#</th>
            <th></th>
            <th style={{ cursor: 'pointer' }} onClick={() => sortUsers('name')}>Name{sortIndicator(sortConfig, 'name')}</th>
            <th style={{ cursor: 'pointer' }} onClick={() => sortUsers('designation')}>Designation{sortIndicator(sortConfig, 'designation')}</th>
            <th>Contact No.</th>
            <th style={{ cursor: 'pointer' }} onClick={() => sortUsers('company')}>Company{sortIndicator(sortConfig, 'company')}</th>
            <th style={{ cursor: 'pointer' }} onClick={() => sortUsers('status')}>Status{sortIndicator(sortConfig, 'status')}</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
        {users.map((employee, index) => (
            <tr key={employee.id}>
              <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
              <td>
                {employee.avatar && (
                  <img
                    src={`${BASE_URL}/uploads/${employee.avatar}`}
                    alt=""
                    width="30"
                    height="30"
                    className="rounded-circle"
                    style={{ objectFit: 'cover' }}
                  />
                )}
              </td>
              <td>{employee.name}</td>
              <td>{employee.designation}</td>
              <td>{employee.contact_no}</td>
              <td>{employee.company}</td>
              <td>
                {(() => {
                  const meta = USER_STATUS_MAP[employee.status] || { label: employee.status, variant: 'secondary' };
                  return <Badge bg={meta.variant}>{meta.label}</Badge>;
                })()}
              </td>
              <td>
                <Link to={`/admin/employee/view/${employee.id}`}>
                  <Button variant="info" className="mr-2">View</Button>
                </Link>
                <Link to={`/users/edit/${employee.id}`}>
                  <Button variant="primary" className="mr-2">Edit</Button>
                </Link>
                {loggedInUser.role !== 'trainingadmin' && (
                  <>
                    {employee.status === 'pending' && (
                      <Link to={`/admin/employee/view/${employee.id}`}>
                        <Button variant="success" className="mr-2">Approve/Reject</Button>
                      </Link>
                    )}
                    {employee.status === 'active' && (
                      <Link to={`/users/assign-training/${employee.id}`}>
                        <Button variant="warning">Assign to Training</Button>
                      </Link>
                    )}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <PaginationControls page={page} totalPages={totalPages} totalRows={totalRows} onPageChange={setPage} />
      </>
      )}
      <Link to="/create-user">
        <Button variant="primary" className="m-1">Add New User</Button>
      </Link>
      <Link to="/users/upload">
        <Button variant="primary" className="m-1">Upload Users</Button>
      </Link>
    </div>
  );
};

export default Users;
